import React, { useMemo, useState } from 'react';
import { PencilIcon, Trash2 } from 'lucide-react';
import { COLORS } from '../Styles/Colors';
import { IconActionButton, PageContainer } from '../Styles/AppStyles';
import { decode } from '../Services/DecodeToken';
import ConfirmationModal from '../Components/Modal/ConfirmationModal';
import apiClient from '../Services/ApiClient';
import { useSnackbar } from '../Context/SnackbarProvider';
import { ColumnDef, DataTable } from '../Components/Table/DataTable';
import { MergeExportDialog } from './Transactions/MergeExportDialog';
import { Header } from '../Components/Header/Header';
import useGetCustomData from '../Hooks/useGetCustomData';
import { DateRangePicker } from '../Components/Date/Datepicker';
import { useColumnSearches } from '../Hooks/useColumnSearch';
import { SearchableHeader } from '../Components/Search/SearchableHeader';
import { LocationSelect } from '../Components/Select/LocationSelect';
import { ItemType, ItemTypeChip, ItemTypeToggle, useItemTypeParam } from '../Components/ItemType/ItemType';

/**
 * Transactions.
 *
 * ABATIX and Non-ABATIX transactions in one list, newest first
 * (GET /api/transactions/combined). Each row keeps its `item_type`, and edits /
 * removals go to that type's existing endpoint with that type's existing rules:
 *   - ABATIX:     edit QTY and PO/Job (PO locked once P21 has an order/invoice);
 *                 locked entirely for invoiced consignment; "Cancel" returns stock.
 *   - Non-ABATIX: edit QTY (> 0) and PO/Job (required); "Delete" returns stock.
 */

interface TransactionRow {
  row_key: string;
  item_type: ItemType;
  source_id: number;
  transaction_date: string;
  CustomerName: string;
  location_name: string;
  po_job_number: string;
  product_code: string;
  product_description: string;
  quantity: number;
  uom: string;
  unit_price: number | string;
  employee_name: string;
  requester_name: string;
  bin_name: string | null;
  category_name: string;
  P21OrderNumber: string | null;
  P21InvoiceNumber: string | null;
  ims_order_no: string | null;
  consignment_flag: number;
  // ABATIX
  TID?: number;
  PID?: number;
  OVID?: number;
  cancelled_flag?: number;
  // Non-ABATIX
  order_item_id?: number;
  order_id?: number;
}

type PendingEdits = Record<string, { row: TransactionRow; fields: { quantity?: number | ''; po_job_number?: string } }>;

const SEARCH_FIELDS = [
  'po_job_number',
  'product_code',
  'bin_name',
  'product_description',
  'location_name',
  'CustomerName',
  'uom',
  'P21OrderNumber',
  'P21InvoiceNumber',
  'ims_order_no',
  'employee_name',
  'requester_name',
  'category_name',
];

const editableHeaderWrapperStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  width: '100%',
  height: '100%',
  paddingTop: '12px',
};

const editableHeaderTitleRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '4px' };

const editableHeaderTitleStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: '#64748b',
  letterSpacing: '0.06em',
  lineHeight: 1,
};

const editableHeaderHintStyle: React.CSSProperties = { fontSize: '10px', color: COLORS.teal, cursor: 'help', lineHeight: 1 };

const isBlank = (v: unknown) => v === '' || v === null || v === undefined;

const isInvoicedConsignment = (row: TransactionRow) =>
  row.item_type === 'abatix' && !!row.P21InvoiceNumber && Number(row.consignment_flag) === 1;

const Transaction: React.FC = () => {
  const token: any = decode();
  const roleLevel = Number(token?.roles?.level) || 0;
  const canEdit = roleLevel >= 50;
  const { showSnackbar } = useSnackbar();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState<string>('transaction_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [ovs] = useGetCustomData('/api/location/user/ovs');
  // Empty string = "All Locations".
  const [selectedOvs, setSelectedOvs] = useState<string>('');
  const [itemType, setItemType] = useItemTypeParam();
  const { columnSearches, setSearch, clearSearch } = useColumnSearches(SEARCH_FIELDS);

  const handleStartDateChange = (d: Date | null) => { setStartDate(d); setPage(0); };
  const handleEndDateChange = (d: Date | null) => { setEndDate(d); setPage(0); };
  const handleColumnSearch = (field: string, value: string) => { setSearch(field, value); setPage(0); };
  const handleClearColumnSearch = (field: string) => { clearSearch(field); setPage(0); };
  const handleTypeChange = (next: typeof itemType) => { setItemType(next); setPage(0); };
  const handleSortChange = (field: string, order: 'asc' | 'desc' | null) => {
    // Clearing the sort goes back to the default: newest first.
    setSortField(field || 'transaction_date');
    setSortOrder(field ? (order ?? 'desc') : 'desc');
    setPage(0);
  };

  const baseUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (itemType !== 'all') params.set('type', itemType);
    if (startDate) params.set('startDate', startDate.toISOString().split('T')[0]);
    if (endDate) params.set('endDate', endDate.toISOString().split('T')[0]);
    if (selectedOvs) params.set('ovid', selectedOvs);
    Object.entries(columnSearches).forEach(([field, value]) => {
      if (value.trim()) params.set(field, value.trim());
    });
    params.set('sort_field', sortField);
    params.set('sort_order', sortOrder);
    return `/api/transactions/combined?${params.toString()}`;
  }, [itemType, startDate, endDate, selectedOvs, columnSearches, sortField, sortOrder]);

  const paginatedUrl = `${baseUrl}&limit=${pageSize}&offset=${page * pageSize}`;
  const [transactions, loading, , refetch] = useGetCustomData(paginatedUrl, { cancelPrevious: true });
  const rows: TransactionRow[] = Array.isArray(transactions?.data) ? transactions.data : [];
  const totalRows = Number(transactions?.totalCount ?? 0);

  // Explain empty results caused by filters that only one type has.
  const oneSidedHint = useMemo(() => {
    const abatixOnly = ['P21OrderNumber', 'P21InvoiceNumber'].some((f) => columnSearches[f]?.trim());
    const nonAbatixOnly = !!columnSearches.ims_order_no?.trim();
    if (abatixOnly && nonAbatixOnly) return 'P21 numbers exist only on ABATIX transactions and IMS order numbers only on Non-ABATIX, so these searches together match nothing.';
    if (abatixOnly) return 'Searching by P21 number shows ABATIX transactions only.';
    if (nonAbatixOnly) return 'Searching by IMS order number shows Non-ABATIX transactions only.';
    return undefined;
  }, [columnSearches]);

  // ─── Editing ──────────────────────────────────────────────────────────────
  const [pending, setPending] = useState<PendingEdits>({});
  const [activeEditRowId, setActiveEditRowId] = useState<string | null>(null);
  const [isEditAllMode, setIsEditAllMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<TransactionRow | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [mergeExportOpen, setMergeExportOpen] = useState(false);

  const canEditRow = (row: TransactionRow) => canEdit && !isInvoicedConsignment(row);
  const canEditPo = (row: TransactionRow) =>
    canEditRow(row) && (row.item_type === 'non-abatix' || (!row.P21OrderNumber && !row.P21InvoiceNumber));

  const setField = (row: TransactionRow, field: 'quantity' | 'po_job_number', value: any) => {
    setPending((prev) => {
      const current = prev[row.row_key] ?? { row, fields: {} };
      return { ...prev, [row.row_key]: { row: current.row, fields: { ...current.fields, [field]: value } } };
    });
  };

  const resetEditing = () => {
    setPending({});
    setActiveEditRowId(null);
    setIsEditAllMode(false);
  };

  const handleSaveChanges = async () => {
    const requests: { row: TransactionRow; send: () => Promise<Response> }[] = [];

    for (const { row, fields } of Object.values(pending)) {
      if (!canEditRow(row)) continue;
      const newQuantity = fields.quantity ?? row.quantity;
      const newPo = fields.po_job_number ?? row.po_job_number;
      const quantityChanged = fields.quantity !== undefined && fields.quantity !== row.quantity;
      const poChanged = fields.po_job_number !== undefined && fields.po_job_number !== row.po_job_number && canEditPo(row);
      if (!quantityChanged && !poChanged) continue;

      if (newQuantity === '' || !Number.isFinite(Number(newQuantity)) || Number(newQuantity) < 0) {
        showSnackbar(`Enter a valid quantity for ${row.product_code}`, 'error');
        return;
      }

      if (row.item_type === 'non-abatix') {
        if (Number(newQuantity) <= 0) {
          showSnackbar(`Quantity must be greater than 0 (${row.product_code})`, 'error');
          return;
        }
        if (!String(newPo ?? '').trim()) {
          showSnackbar(`PO/Job No. is required (${row.product_code})`, 'error');
          return;
        }
        requests.push({
          row,
          send: () => apiClient('/api/non-abatix/transactions', {
            method: 'PUT',
            body: JSON.stringify({
              order_item_id: row.order_item_id,
              order_id: row.order_id,
              quantity: Number(newQuantity),
              po_job_number: newPo,
            }),
          }),
        });
      } else {
        requests.push({
          row,
          send: () => apiClient('/api/transactions', {
            method: 'PUT',
            body: JSON.stringify({
              TID: row.TID,
              quantity: Number(newQuantity),
              // null = leave unchanged (what this page has always sent for edits).
              cancelled_flag: null,
              productCode: row.product_code,
              PID: row.PID,
              OVID: row.OVID,
              ...(poChanged ? { po_job_number: newPo } : {}),
            }),
          }),
        });
      }
    }

    if (requests.length === 0) {
      showSnackbar('No changes to save', 'error');
      return;
    }

    setSaving(true);
    try {
      const results = await Promise.all(requests.map(async ({ row, send }) => {
        try {
          const response = await send();
          return { row, ok: response.ok, status: response.status };
        } catch {
          return { row, ok: false, status: 0 };
        }
      }));

      const failed = results.filter((r) => !r.ok);
      if (failed.length === 0) {
        showSnackbar('Changes saved successfully', 'success', 'Edit Success!');
        resetEditing();
      } else {
        // Keep the edits that did not save; drop the ones that did.
        const failedKeys = new Set(failed.map((r) => r.row.row_key));
        setPending((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => failedKeys.has(key))));
        const invoiced = failed.some((r) => r.status === 403 && r.row.item_type === 'abatix');
        const forbidden = failed.some((r) => r.status === 403 && r.row.item_type === 'non-abatix');
        const message = invoiced
          ? 'Cannot edit — transaction already invoiced in P21'
          : forbidden
            ? 'Insufficient permissions to edit transactions'
            : 'Some updates failed. Please check and try again.';
        showSnackbar(failed.length < results.length ? `Some changes saved. ${message}` : message, 'error');
      }
      refetch();
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmRemove = async () => {
    if (!removeTarget || isRemoving) return;
    setIsRemoving(true);
    const isNonAbatix = removeTarget.item_type === 'non-abatix';
    try {
      const response = isNonAbatix
        ? await apiClient(`/api/non-abatix/transactions/${removeTarget.order_item_id}`, { method: 'DELETE' })
        : await apiClient('/api/transactions', {
            method: 'PUT',
            body: JSON.stringify({
              TID: removeTarget.TID,
              quantity: removeTarget.quantity,
              cancelled_flag: 1,
              productCode: removeTarget.product_code,
              PID: removeTarget.PID,
              OVID: removeTarget.OVID,
            }),
          });

      if (response.status === 403) {
        showSnackbar(
          isNonAbatix ? 'Insufficient permissions to delete transactions' : 'Cannot cancel — transaction already invoiced in P21',
          'error',
        );
        setRemoveTarget(null);
        return;
      }
      if (!response.ok) {
        showSnackbar(isNonAbatix ? 'Failed to delete transaction' : 'Failed to cancel transaction', 'error');
        return;
      }
      showSnackbar(isNonAbatix ? 'Transaction deleted' : 'Transaction cancelled', 'success');
      setPending((prev) => {
        const next = { ...prev };
        delete next[removeTarget.row_key];
        return next;
      });
      setRemoveTarget(null);
      refetch();
    } catch {
      showSnackbar(isNonAbatix ? 'Failed to delete transaction' : 'Failed to cancel transaction', 'error');
    } finally {
      setIsRemoving(false);
    }
  };

  // ─── Cells ────────────────────────────────────────────────────────────────

  const renderEditableCell = (row: TransactionRow, field: 'quantity' | 'po_job_number') => {
    const isQty = field === 'quantity';
    const original = row[field] ?? (isQty ? 0 : '');
    const edits = pending[row.row_key]?.fields;
    const editedValue = edits?.[field];
    const edited = editedValue !== undefined && editedValue !== original;
    const display = edited ? editedValue : original;
    const editable = isQty ? canEditRow(row) : canEditPo(row);

    if (!editable) {
      return (
        <div
          style={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%', padding: '0 8px', cursor: 'default' }}
          title={isInvoicedConsignment(row)
            ? 'Already invoiced in P21'
            : !isQty && row.item_type === 'abatix' && (row.P21OrderNumber || row.P21InvoiceNumber)
              ? 'PO/Job No. is locked once P21 has the order'
              : undefined}
        >
          <span>{isBlank(display) ? '-' : display}</span>
        </div>
      );
    }

    const isActive = isQty ? (isEditAllMode || activeEditRowId === row.row_key) : activeEditRowId === row.row_key;

    return (
      <div
        data-editable-cell
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          padding: '0 8px',
          backgroundColor: edited ? `${COLORS.teal}15` : 'transparent',
          cursor: 'pointer',
          borderRadius: '4px',
          transition: 'background-color 0.2s ease',
        }}
        onClick={() => setActiveEditRowId(row.row_key)}
        onMouseEnter={(e) => { if (!isActive && !edited) e.currentTarget.style.backgroundColor = `${COLORS.teal}08`; }}
        onMouseLeave={(e) => { if (!isActive && !edited) e.currentTarget.style.backgroundColor = 'transparent'; }}
        title="Click to edit"
      >
        {isActive ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
            {edited && <span style={{ fontSize: '8px', color: COLORS.teal, fontWeight: 'bold', flexShrink: 0 }}>●</span>}
            <input
              type={isQty ? 'number' : 'text'}
              min={isQty ? 0 : undefined}
              aria-label={`${isQty ? 'Quantity' : 'PO/Job No.'} for ${row.product_code}`}
              value={isBlank(display) ? '' : String(display)}
              onChange={(e) => {
                const raw = e.target.value;
                setField(row, field, isQty ? (raw === '' ? '' : Number(raw)) : raw);
              }}
              onClick={(e) => e.stopPropagation()}
              autoFocus={!isEditAllMode && isQty}
              style={{
                width: '100%',
                borderRadius: '4px',
                padding: '4px',
                outline: 'none',
                border: edited ? `1px solid ${COLORS.teal}` : 'none',
                backgroundColor: 'white',
              }}
              onKeyDown={(e) => {
                if (isQty && (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+')) e.preventDefault();
                if (e.key === 'Enter' || e.key === 'Escape') setActiveEditRowId(null);
              }}
            />
          </div>
        ) : (
          <span style={{ fontWeight: edited ? 'bold' : 'normal', color: edited ? COLORS.teal : 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}>
            {edited && <span style={{ fontSize: '8px', color: COLORS.teal, fontWeight: 'bold' }}>●</span>}
            {isBlank(display) ? '-' : display}
          </span>
        )}
      </div>
    );
  };

  const searchHeader = (field: string, headerName: string) => () => (
    <SearchableHeader
      field={field}
      headerName={headerName}
      searchValue={columnSearches[field]}
      onSearchChange={handleColumnSearch}
      onClearSearch={handleClearColumnSearch}
    />
  );

  const columns: ColumnDef<any>[] = [
    {
      field: 'transaction_date',
      headerName: 'Date',
      width: 92,
      sortable: true,
      valueGetter: (value: any) => value ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC', month: '2-digit', day: '2-digit', year: 'numeric' }) : '-',
      renderCell: (params: any) => params.value,
    },
    { field: 'CustomerName', headerName: 'Account', width: 180, sortable: true, renderHeader: searchHeader('CustomerName', 'Account') },
    { field: 'location_name', headerName: 'Location', width: 200, sortable: true, renderHeader: searchHeader('location_name', 'Location') },
    {
      field: 'po_job_number',
      headerName: 'PO/Job No.',
      width: 140,
      sortable: true,
      renderCell: (params: any) => renderEditableCell(params.row, 'po_job_number'),
      renderHeader: searchHeader('po_job_number', 'PO/Job No.'),
    },
    { field: 'product_code', headerName: 'Part No.', width: 160, sortable: true, renderHeader: searchHeader('product_code', 'Part No.') },
    { field: 'product_description', headerName: 'Description', width: 340, sortable: true, renderHeader: searchHeader('product_description', 'Description') },
    {
      field: 'quantity',
      headerName: 'QTY',
      width: 88,
      sortable: true,
      type: 'number',
      renderCell: (params: any) => renderEditableCell(params.row, 'quantity'),
      renderHeader: () => (
        <div style={{ ...editableHeaderWrapperStyle, ...(isEditAllMode ? { backgroundColor: `${COLORS.teal}08` } : {}) }}>
          <div style={editableHeaderTitleRowStyle}>
            <span style={editableHeaderTitleStyle}>QTY</span>
            <span style={editableHeaderHintStyle} title={isEditAllMode ? 'All rows are editable' : 'Click cell to edit'}>✎</span>
          </div>
        </div>
      ),
    },
    { field: 'uom', headerName: 'UOM', width: 64, sortable: true, renderHeader: searchHeader('uom', 'UOM') },
    { field: 'unit_price', headerName: 'Price', width: 70, sortable: true },
    {
      field: 'ext_price',
      headerName: 'Ext. Price',
      width: 90,
      sortable: true,
      valueGetter: (_value: any, row: any) => {
        const edited = pending[row.row_key]?.fields.quantity;
        const qty = edited !== undefined && edited !== '' ? Number(edited) : Number(row?.quantity ?? 0);
        return (qty * Number(row?.unit_price ?? 0)).toFixed(2);
      },
      renderCell: (params: any) => params.value,
    },
    { field: 'employee_name', headerName: 'Employee', width: 130, sortable: true, renderHeader: searchHeader('employee_name', 'Employee') },
    { field: 'requester_name', headerName: 'Requester', width: 130, sortable: true, renderHeader: searchHeader('requester_name', 'Requester') },
    {
      field: 'bin_name',
      headerName: 'Bin',
      width: 110,
      sortable: true,
      renderCell: (params: any) => params.row.bin_name || '-',
      renderHeader: searchHeader('bin_name', 'Bin'),
    },
    { field: 'category_name', headerName: 'Category', width: 150, sortable: true, renderHeader: searchHeader('category_name', 'Category') },
    {
      field: 'P21OrderNumber',
      headerName: 'P21 Order No.',
      width: 140,
      sortable: true,
      renderCell: (params: any) => params.row.P21OrderNumber || '-',
      renderHeader: searchHeader('P21OrderNumber', 'P21 Order No.'),
    },
    {
      field: 'P21InvoiceNumber',
      headerName: 'P21 Invoice No.',
      width: 140,
      sortable: true,
      renderCell: (params: any) => params.row.P21InvoiceNumber || '-',
      renderHeader: searchHeader('P21InvoiceNumber', 'P21 Invoice No.'),
    },
    {
      field: 'ims_order_no',
      headerName: 'IMS Order No.',
      width: 140,
      sortable: true,
      renderCell: (params: any) => params.row.ims_order_no || '-',
      renderHeader: searchHeader('ims_order_no', 'IMS Order No.'),
    },
    {
      field: 'item_type',
      headerName: 'Type',
      width: 118,
      sortable: true,
      filterable: false,
      renderCell: (params: any) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <ItemTypeChip type={params.row.item_type} />
        </div>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 84,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        const row = params.row as TransactionRow;
        const locked = isInvoicedConsignment(row);
        const isNonAbatix = row.item_type === 'non-abatix';
        const editTitle = !canEdit
          ? 'Insufficient permissions'
          : locked ? 'Cannot edit — transaction already invoiced in P21' : 'Edit QTY and PO/Job No.';
        const removeTitle = !canEdit
          ? 'Insufficient permissions'
          : locked
            ? 'Cannot cancel — transaction already invoiced in P21'
            : isNonAbatix ? 'Delete transaction (returns quantity to inventory)' : 'Cancel transaction (returns quantity to inventory)';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '100%' }}>
            <IconActionButton
              disabled={!canEditRow(row)}
              onClick={() => canEditRow(row) && setActiveEditRowId(row.row_key)}
              title={editTitle}
              aria-label={editTitle}
            >
              <PencilIcon />
            </IconActionButton>
            <IconActionButton
              disabled={!canEditRow(row)}
              onClick={() => canEditRow(row) && setRemoveTarget(row)}
              $variant="danger"
              title={removeTitle}
              aria-label={removeTitle}
            >
              <Trash2 size={16} />
            </IconActionButton>
          </div>
        );
      },
    },
  ];

  const editedCount = Object.keys(pending).length;
  const removeIsDelete = removeTarget?.item_type === 'non-abatix';
  const removeBin = removeTarget?.bin_name ? ` (Bin: ${removeTarget.bin_name})` : '';

  return (
    <PageContainer>
      <Header title="Transactions" subtitle={oneSidedHint} count={totalRows} hideCount={false}>
        <DateRangePicker startDate={startDate} endDate={endDate} setStartDate={handleStartDateChange} setEndDate={handleEndDateChange} />
      </Header>

      {removeTarget && (
        <ConfirmationModal
          title={removeIsDelete ? 'Delete Transaction?' : 'Cancel Transaction?'}
          message={`${removeIsDelete ? 'Delete' : 'Cancel'} ${removeTarget.product_code} — qty ${removeTarget.quantity}${removeBin}? The quantity will be returned to inventory.`}
          confirmText={removeIsDelete ? 'Delete Transaction' : 'Cancel Transaction'}
          variant="danger"
          isLoading={isRemoving}
          loadingText={removeIsDelete ? 'Deleting...' : 'Cancelling...'}
          onConfirm={handleConfirmRemove}
          onCancel={() => { if (!isRemoving) setRemoveTarget(null); }}
        />
      )}

      <DataTable
        data={rows}
        loading={loading || saving}
        columns={columns}
        pageKey="transactions-v2"
        defaultColumnVisibility={{ CustomerName: false, category_name: false }}
        serverSidePagination={true}
        page={page}
        rowCount={totalRows}
        idField="row_key"
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        initialPageSize={pageSize}
        hideColumnFilters={true}
        hideSearchBar={true}
        serverSideSort={true}
        defaultSort={[{ field: 'transaction_date', sort: 'desc' }]}
        onSortModelChange={handleSortChange}
        onMergeExport={() => setMergeExportOpen(true)}
        onEditAll={canEdit ? () => setIsEditAllMode(true) : undefined}
        editAllTooltip="Edit QTY for all rows on this page at once"
        isEditAllMode={isEditAllMode}
        isRowEditMode={!!activeEditRowId}
        hasEdits={editedCount > 0}
        editedCount={editedCount}
        onSaveChanges={handleSaveChanges}
        onCancelChanges={resetEditing}
      >
        <LocationSelect
          ovs={ovs}
          selectedOvs={selectedOvs}
          onChange={(value) => { setSelectedOvs(value === null || value === undefined ? '' : String(value)); setPage(0); }}
          setDefault={true}
        />
        <ItemTypeToggle value={itemType} onChange={handleTypeChange} />
      </DataTable>

      <MergeExportDialog
        open={mergeExportOpen}
        onClose={() => setMergeExportOpen(false)}
        initialFilters={{ startDate, endDate, selectedOvs, columnSearches, itemType }}
      />
    </PageContainer>
  );
};

export default Transaction;
